import anime from "animejs";
import {
  setShowFreeSpinIntro,
  setShowHoldAndWinIntro,
  setShowTransitionDialog,
} from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import TickerTimeout from "../../../utils/TickerTimeout";
import placeTapToContinue from "../../../utils/placeTapToContinue";
import { TAP_TO_CONTINUE_HIT_HEIGHT } from "../../../utils/attachTapToContinue";
import store from "../../../State/Store/store";
import FeatureIntroCard, {
  TAP_ANCHOR,
  fitCardText,
  splitCountSentence,
} from "./FeatureIntroCard";

/**
 * FeatureIntroController — the bonus INTRO card, one per feature.
 *
 *   free_spins    the free-spins board picture
 *   hold_and_win  the 5x8 board + the Royal Jackpot badge
 *
 * The card is static art (FeatureIntroCard) playing the old spine's clips.
 *
 * Copy (the design, 2026-10-09), line: text — key:
 *
 *   Free Spins
 *     congrats/lead/count/label  CONGRATULATIONS! / YOU WON / 8 / FREE SPINS
 *                                — congrats_you_won_free_spins (shared)
 *     support                    ONLY HIGH-PAYING SYMBOLS — feature_intro_fs_support
 *   Hold & Win
 *     congrats                   CONGRATULATIONS! — the FS sentence's first line
 *                                (so both cards say it the same in every language)
 *     lead                       YOU WON — you_won
 *     title                      CLEOPATRA'S RESPINS — feature_intro_hnw_title
 *     support                    COLLECT COINS TO UNLOCK ROWS — feature_intro_hnw_support
 *   Both
 *     tap                        TAP TO START — tap_to_start
 *
 * Sequence: `in` -> `loop`. A tap (or the spacebar) starts the transition wipe
 * at once; the 10 s auto-close (GDD §11.5 / §12) waits for the end of the
 * current loop. The wipe plays ON TOP of the card and the card goes at the
 * wipe's midpoint, under the cover. The intro never plays `out`.
 *
 * The reloads use their own dialogs (HoldAndWinIntroController /
 * FreeSpinIntroDialogController); the FS retrigger draws this card too
 * (AdditionalFreeSpinsController).
 */

const AUTO_HIDE_MS = 10000;
// Net for the wipe's midpoint: the wipe is 2 s, its midpoint at 1 s.
const WIPE_MIDPOINT_NET_MS = 2500;

// Card scale per device, on top of the symbol-size scale.
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.0, buttonYOffset: 0.0 },
  MOBILE_SMALL_LANDSCAPE: { scale: 1.0, buttonYOffset: 0.0 },
  MOBILE_PORTRAIT: { scale: 1.0, buttonYOffset: 0.0 },
  // MOBILE_LANDSCAPE: { scale: 1.0, buttonYOffset: 0.0 },
  // A bit smaller (2026-10-16): the card follows the symbol size, the bigger
  // landscape reel grew it, and the title touched the top edge.
  MOBILE_LANDSCAPE: { scale: 0.92, buttonYOffset: 0.0 },
  TABLET_PORTRAIT: { scale: 1.0, buttonYOffset: 0.0 },
  TABLET_LANDSCAPE: { scale: 1.0, buttonYOffset: 0.0 },
  DESKTOP: { scale: 0.9, buttonYOffset: 0.0 },
};

// The gap between the end of the description and the tap word's hit area.
const SUPPORT_TAP_GAP = 16;

class FeatureIntroController {
  constructor(container, dimensionsContext, dispatch, symbolHeight, overlayRef, t, feature) {
    this.feature = feature === "hold_and_win" ? "hold_and_win" : "free_spins";
    this.isHoldAndWin = this.feature === "hold_and_win";
    // The dialogs read this to tell a reload controller from an intro one.
    this.isReload = false;
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.card = null;
    this.initialized = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.autoHideTimeout = null;
    this._showRAF = null;

    this.buttonContainer = null;
    this.buttonText = null;
    this.texts = {};
    this._count = this.isHoldAndWin ? 3 : 8;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
    this._lastIsMobileSmall = null;

    this.initializeCard();
  }

  // ── Sizing ──

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }
    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let key = "DESKTOP";
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) key = isPortrait ? "MOBILE_SMALL_PORTRAIT" : "MOBILE_SMALL_LANDSCAPE";
      else key = isPortrait ? "MOBILE_PORTRAIT" : "MOBILE_LANDSCAPE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      key = isPortrait ? "TABLET_PORTRAIT" : "TABLET_LANDSCAPE";
    }
    this._configCache = DEVICE_MULTIPLIERS[key];
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return this._configCache;
  }

  _isLandscape() {
    return this.dimensionsContext?.orientation !== PixiSettings.ORIENTATION.PORTRAIT;
  }

  applyScaleAndCenter() {
    if (!this.card || !this.dimensions) return;
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const scale =
      Math.max(0.1, Math.min(targetSize / nativeSize, 2)) * this.getDeviceMultipliers().scale;
    this.card.view.scale.set(scale);
    this.card.view.position.set(0, 0);
    // The background for this orientation (FeatureIntroCard.setLandscape).
    this.card.setLandscape?.(this._isLandscape());
    this._fitTapButton?.(this.dimensions);
    this._setTapOffsetY?.(this.getDeviceMultipliers().buttonYOffset ?? 0);
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const deviceChanged = this.dimensionsContext?.device !== dimensionsContext?.device;
    const changed =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      deviceChanged ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;
    if (!changed) return;
    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;
    if (deviceChanged) this.updateButtonText();
    if (this.symbolHeight > 0) this.applyScaleAndCenter();
    // The description re-flows for the new orientation (wider in landscape).
    const landscape = this._isLandscape();
    Object.values(this.texts).forEach((textObj) => fitCardText(textObj, landscape));
    this._fitSupport();
  }

  // ── Card ──

  initializeCard() {
    try {
      this.card = new FeatureIntroCard(this.feature);
      this.card.view.alpha = 0;
      this.card.view.zIndex = 20;
      this.container.addChild(this.card.view);
      this.card.warmUp();
      this.initialized = true;
      this.initializeButton();
      this.initializeTexts();
      this.applyScaleAndCenter();
    } catch (error) {
      console.warn("[FeatureIntro] Init failed:", error);
    }
  }

  // ── Texts ──

  initializeButton() {
    const built = placeTapToContinue({
      holder: this.card.holder("tap"),
      width: TAP_ANCHOR.width,
      label: this._buttonLabel(),
      name: "feature_intro_continue",
      onClick: () => {
        if (this.handleUserClick()) soundManager.play(SOUND_NAME.tap_to_continue);
      },
      onFit: () => this._fitSupport(),
    });
    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._setTapOffsetY = built.setOffsetY;
    this.buttonContainer.alpha = 0;
  }

  _buttonLabel() {
    return this.t("tap_to_start").toUpperCase();
  }

  updateButtonText() {
    if (!this.buttonText) return;
    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  /** The copy lines this feature shows, above and under the board. */
  _copyLines() {
    return this.isHoldAndWin
      ? { above: ["congrats", "lead"], below: ["title"] }
      : { above: ["congrats", "lead", "count"], below: ["label"] };
  }

  _copyLineNames() {
    const { above, below } = this._copyLines();
    return [...above, ...below];
  }

  /** The copy — see the table in the header. Upper-cased. */
  _lines() {
    const fsSentence = this.t("congrats_you_won_free_spins", { count: this._count });
    if (this.isHoldAndWin) {
      return {
        congrats: splitCountSentence(fsSentence, this._count).congrats,
        // lead: this.t("you_won").toUpperCase(),
        // The shared "YOU WON" (2026-10-13 — no game-local copy).
        lead: this.t("popup_respins_feature_entry_title").toUpperCase(),
        title: this.t("feature_intro_hnw_title").toUpperCase(),
        support: this.t("feature_intro_hnw_support").toUpperCase(),
      };
    }
    const { congrats, lead, label } = splitCountSentence(fsSentence, this._count);
    return {
      congrats,
      lead,
      count: String(this._count),
      label,
      support: this.t("feature_intro_fs_support").toUpperCase(),
    };
  }

  initializeTexts() {
    const lines = this._lines();
    const landscape = this._isLandscape();
    this.card.setCopyLines(this._copyLines());
    for (const name of [...this._copyLineNames(), "support"]) {
      this.texts[name] = this.card.addText(name, lines[name], landscape);
    }
    this._fitSupport();
  }

  /** The description ends above the tap word, wherever the tap line put it. */
  _fitSupport() {
    if (!this.card || !this.texts.support) return;
    const tapY = TAP_ANCHOR.y + (this.buttonContainer?.y ?? 0);
    const bottom = tapY - TAP_TO_CONTINUE_HIT_HEIGHT / 2 - SUPPORT_TAP_GAP;
    this.card.setSupportHeight(this.texts.support, bottom, this._isLandscape());
  }

  /** FS: the spins won (the sentence follows it). H&W shows no count. */
  updateFreeSpinCount(count) {
    if (count > 0) this._count = count;
    const lines = this._lines();
    const landscape = this._isLandscape();
    for (const name of this._copyLineNames()) {
      const textObj = this.texts[name];
      if (!textObj) continue;
      textObj.text = lines[name];
      fitCardText(textObj, landscape);
    }
  }

  updateRespins(count) {
    this.updateFreeSpinCount(count);
  }

  /** The FS intro dialog sets the package tier; the card has no tiers. */
  setTier() {}

  showButton() {
    if (!this.buttonContainer) return;
    anime({ targets: this.buttonContainer, alpha: 1, duration: 300, easing: "easeOutQuad" });
  }

  // ── Sequence ──

  startSequence() {
    if (this.isPlaying || !this.card) return;
    this.isPlaying = true;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.overlayRef.current?.fadeInBackground();
    this._clearTimers();
    soundManager.play(SOUND_NAME.free_spin_intro);

    if (this.symbolHeight > 0 && this.dimensions) this.applyScaleAndCenter();

    this.skipProtectionTimeout = new TickerTimeout(() => {
      if (!this.isPlaying) return;
      this.canSkipAnimation = true;
      this.showButton();
      this.isWaitingForClick = true;
      this._armAutoHide();
    }, this.card.revealMs("in"));

    this.card.play("in", {
      onComplete: () => {
        if (!this.isPlaying) return;
        this.card.play("loop", { loop: true, onLoop: () => this._onLoopSeam?.() });
      },
    });
    // Shown on the next frame, when the clip's first pose is on the card.
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.card) this.card.view.alpha = 1;
    });
  }

  handleUserClick() {
    if (!this.isPlaying || !this.card) return false;
    if (this.debounceTimeout || !this.canSkipAnimation || !this.isWaitingForClick) return false;
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);
    this.playClosingAnimation();
    return true;
  }

  _armAutoHide() {
    this.autoHideTimeout?.clear();
    // Off by the feature (FEATURE_INTRO.AUTO_CLOSE, 2026-10-23): only the tap
    // closes it. This is the intro a bonus won in play shows — Free Spins and
    // Hold & Win alike (the reload cards have their own controllers).
    if (!FEATURES_CONFIG.FEATURE_INTRO?.AUTO_CLOSE) return;
    this.autoHideTimeout = new TickerTimeout(() => {
      this.autoHideTimeout = null;
      if (this.isPlaying && this.isWaitingForClick) {
        this.playClosingAnimation({ atSeam: true });
      }
    }, AUTO_HIDE_MS);
  }

  /**
   * Close: the card keeps looping and the wipe plays on top of it. A tap
   * starts the wipe at once; `atSeam` (the auto-close) waits for the end of
   * the current loop, with a net of one whole loop.
   */
  playClosingAnimation({ atSeam = false } = {}) {
    if (!this.isWaitingForClick || !this.card) return;
    this.autoHideTimeout?.clear();
    this.autoHideTimeout = null;
    this.isWaitingForClick = false;
    // No close sound (2026-10-22): the intro opens with its sound and the
    // transition comes on top — nothing closes on its own.
    // soundManager.play(SOUND_NAME.free_spin_intro_close);
    if (this.buttonContainer) {
      anime({ targets: this.buttonContainer, alpha: 0, duration: 200, easing: "easeOutQuad" });
    }

    this._closing = true;
    if (!atSeam) {
      this._startWipe();
      return;
    }
    this._onLoopSeam = () => this._startWipe();
    this._seamTimeout = new TickerTimeout(
      () => this._startWipe(),
      this.card.duration("in") + this.card.duration("loop") + 100,
    );
  }

  /** The wipe on top of the card; the card goes at its midpoint. Once. */
  _startWipe() {
    if (!this._closing || this._wipeStarted || !this.isPlaying) return;
    this._wipeStarted = true;
    this._dropSeamWatch();

    // Only for a real trigger — the dev panel's preview just closes.
    const state = store.getState();
    const triggered = this.isHoldAndWin
      ? state.holdAndWin?.triggered
      : state.freeSpins?.triggered || !FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL;
    if (!triggered) {
      this.finishAnimation();
      return;
    }
    this.dispatch(setShowTransitionDialog(true));

    // The midpoint raises the room flag (TransitionDialog) — the frame the
    // cover is opaque.
    const roomUp = () => {
      const st = store.getState();
      return this.isHoldAndWin ? st.holdAndWin?.active : st.freeSpins?.active;
    };
    this._unsubscribeMidpoint = store.subscribe(() => {
      if (roomUp()) this.finishAnimation();
    });
    this._midpointTimeout = new TickerTimeout(() => this.finishAnimation(), WIPE_MIDPOINT_NET_MS);
  }

  _dropSeamWatch() {
    this._onLoopSeam = null;
    this._seamTimeout?.clear();
    this._seamTimeout = null;
  }

  _dropMidpointWatch() {
    this._unsubscribeMidpoint?.();
    this._unsubscribeMidpoint = null;
    this._midpointTimeout?.clear();
    this._midpointTimeout = null;
  }

  finishAnimation() {
    if (!this.card || !this.initialized) return;
    this._dropSeamWatch();
    this._dropMidpointWatch();
    this._closing = false;
    this._wipeStarted = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.card.stop();
    this.card.view.alpha = 0;
    if (this.buttonContainer) this.buttonContainer.alpha = 0;
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    soundManager.stop(SOUND_NAME.free_spin_intro);
    this._clearTimers();
    this.overlayRef.current?.fadeOutBackground(() => {
      this.dispatch?.(
        this.isHoldAndWin ? setShowHoldAndWinIntro(false) : setShowFreeSpinIntro(false),
      );
    });
  }

  _clearTimers() {
    for (const key of ["autoHideTimeout", "skipProtectionTimeout", "debounceTimeout"]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._dropSeamWatch();
    this._dropMidpointWatch();
    soundManager.stop(SOUND_NAME.free_spin_intro);
    this._clearTimers();
    if (this.card) {
      if (this.container?.children.includes(this.card.view)) {
        this.container.removeChild(this.card.view);
      }
      this.card.destroy();
      this.card = null;
    }
    this.buttonContainer = null;
    this.buttonText = null;
    this.texts = {};
    this.initialized = false;
  }
}

export default FeatureIntroController;
